import type { Choice } from '@monthio/design-system'
import type { FunctionComponent } from 'react'

import {
	CurrencyInput,
	H2,
	MultipleChoice,
	Note,
	SPACING,
} from '@monthio/design-system'
import {
	formatCurrencyForDisplay,
	useIntlConfig,
} from '@monthio/react-components'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { styled } from 'styled-components'

import type {
	IncomeChain,
	IncomeVerificationFormData,
} from '../IncomeVerification.types'

import { IncomeChainAssociationEnum } from '../IncomeVerification.types'
import {
	ChainInputWrapper,
	ChainWrapper,
	TransactionRecordListCard,
} from './IncomeVerificationComponents'
import { hasLargeDifferenceInput } from './incomeVerificationUtils'

interface IncomeChainProps {
	values: IncomeVerificationFormData
	initialChain: IncomeChain
	chainIndex: number
	expandAssociationOwnerQuestionaire: boolean
	setFieldValues: any
	errors: any
}

const currencyFormatterOptions = {
	decimalScale: 2,
	decimalsLimit: 2,
	allowNegativeValue: false,
} as const

export const IncomeVerificationChain: FunctionComponent<IncomeChainProps> = ({
	values,
	initialChain,
	chainIndex,
	setFieldValues,
	expandAssociationOwnerQuestionaire,
}) => {
	const { t } = useTranslation()
	const intlConfig = useIntlConfig({ showCurrency: true })
	const formikChain = values.incomeChains[chainIndex]

	useEffect(() => {
		setFieldValues(
			`incomeChains[${chainIndex}].hasLargeInputDifferences`,
			hasLargeDifferenceInput(
				formikChain?.assessment?.value,
				initialChain.assessment?.value,
			),
		)
	}, [
		formikChain?.assessment?.value,
		chainIndex,
		initialChain.assessment?.value,
		setFieldValues,
	])

	const handleBlur = useCallback(
		(e) => {
			if (e.target.value === '') {
				setFieldValues(
					`incomeChains[${chainIndex}].assessment.value`,
					initialChain.assessment?.value,
				)
			}
		},
		[chainIndex, initialChain.assessment?.value, setFieldValues],
	)

	const handleQuestionaireClick = useCallback(
		(v: string) => {
			setFieldValues(`incomeChains[${chainIndex}].owner`, parseInt(v))
		},
		[chainIndex, setFieldValues],
	)

	const handleChange = useCallback(
		(value) => {
			setFieldValues(`incomeChains[${chainIndex}].assessment.value`, value)
		},
		[chainIndex, setFieldValues],
	)

	const associationOwnerChoices: Choice[] = useMemo(() => {
		return [
			{
				value: IncomeChainAssociationEnum.Mine.toString(),
				label: t('incomeVerification-chain-association-option-mine'),
			},
			{
				value: IncomeChainAssociationEnum.Partner.toString(),
				label: t('incomeVerification-chain-association-option-partner'),
			},
			{
				value: IncomeChainAssociationEnum.Unknown.toString(),
				label: t('incomeVerification-chain-association-option-unknown'),
			},
		]
	}, [t])

	const { locale, currency = 'DKK' } = intlConfig
	return (
		<ChainWrapper key={`INCOME_VERIFICATION_STREAM_ID_${initialChain.id}`}>
			<ChainInputWrapper>
				<CurrencyInputWrapper>
					<H2 id="salary-heading">
						{t(
							`incomeVerification-monthly-income-after-tax-${
								initialChain?.typeOfIncome?.description?.toLowerCase() ??
								'fallback'
							}`,
						)}
					</H2>
					<CurrencyInput
						id="income-chain-currency-input"
						aria-describedby="income-variation-note"
						aria-labelledby="salary-heading"
						currency={currency}
						locale={locale}
						onChange={handleChange}
						onBlur={handleBlur}
						value={formikChain?.assessment?.value ?? 0}
					/>
				</CurrencyInputWrapper>
				{initialChain.highVariability ? (
					<Note variant="info" id="income-variation-note">
						{t('incomeVerification-estimatedIncomeExplainer', {
							maxValue: formatCurrencyForDisplay({
								value: String(initialChain.range.max),
								intlConfig,
								...currencyFormatterOptions,
								decimalsLimit: 0,
							}),
							minValue: formatCurrencyForDisplay({
								value: String(initialChain.range.min),
								intlConfig,
								...currencyFormatterOptions,
								decimalsLimit: 0,
							}),
						})}
					</Note>
				) : null}
			</ChainInputWrapper>

			<TransactionRecordListCard
				chainId={initialChain.id}
				transactions={initialChain.transactions}
				title={t('incomeVerification-transactions-title')}
				locale={locale}
				currency={currency}
			/>

			{expandAssociationOwnerQuestionaire ? (
				<ChainInputWrapper>
					<MultipleChoice
						isRequired
						requiredText={t('required')}
						title={t('incomeVerification-chain-association-owner')}
						fieldName={`incomeChains[${chainIndex}].owner`}
						type="radio"
						selected={formikChain?.owner?.toString()}
						choices={associationOwnerChoices}
						onChange={handleQuestionaireClick}
					/>
				</ChainInputWrapper>
			) : null}
		</ChainWrapper>
	)
}

const CurrencyInputWrapper = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: space-between;
	padding: ${SPACING.xxs} 0;
`
